import { Address, formatAddress, getValidatedAddress, type ValidAddress } from "@inrev/common";
import { QueryKey, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import JSZip from "jszip";
import { useEffect, useRef, useState } from "react";
import { FieldError } from "react-hook-form";
import { downloadFileFromDataBlob, downloadFileFromDataString } from "./utils/download";
import { useRequest } from "./utils/request";

export const getFromValidatedAddressCache = (address: string): Address | undefined => {
	const cachedResponse = sessionStorage.getItem(address);
	if (cachedResponse !== null) return JSON.parse(cachedResponse);
};

export const setValidatedAddressCache = (addressKey: string, addressValue: Address) => {
	sessionStorage.setItem(addressKey, JSON.stringify(addressValue));
};

export type AddressFormFieldControl = {
	value: string | undefined;
	onChange?: (value: string) => void;
	onBlur?: () => void;
	error?: FieldError;
};

export type UseAddressValidationReturn = {
	validatedAddress: Address | undefined;
	validateAddressIsLoading: boolean;
	validateAddressAttempted: boolean;
	validateAddress: () => Promise<void>;
	addressFieldControl: AddressFormFieldControl;
	setAddressValue: (value: string) => void;
};

export const useAddressValidation = ({
	value,
	error,
	setValue,
	setError,
	clearError,
}: {
	value: string | undefined;
	error: FieldError | undefined;
	setValue: (value: string) => void;
	setError: (error: FieldError) => void;
	clearError: () => void;
}): UseAddressValidationReturn => {
	const mountedRef = useRef(true);
	const initialValueRef = useRef(value);
	const initialErrorRef = useRef(error);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const [
		{ validatedAddress, validateAddressIsLoading, validateAddressAttempted },
		setLoadingState,
	] = useState<{
		validatedAddress: ValidAddress | undefined;
		validateAddressIsLoading: boolean;
		validateAddressAttempted: boolean;
	}>({
		validatedAddress: undefined,
		validateAddressIsLoading: false,
		validateAddressAttempted: false,
	});

	const validateAddress = async () => {
		setLoadingState({
			validatedAddress,
			validateAddressAttempted: true,
			validateAddressIsLoading: true,
		});
		getValidatedAddress(value).then((response) => {
			if (response === undefined) {
				setError({ type: "error", message: "Invalid address" });
			}
			setLoadingState({
				validatedAddress: response as ValidAddress,
				validateAddressAttempted: true,
				validateAddressIsLoading: false,
			});
		});
	};

	const handleBlur = () => {
		if (value !== "") {
			validateAddress();
		} else if (initialValueRef.current === "") {
			if (!initialErrorRef.current) clearError();
			else setError(initialErrorRef.current);
		} else {
			clearError();
		}
	};

	const handleChange = (newValue: string) => {
		if (newValue !== value) {
			setValue(newValue);
		}
	};

	const addressFieldControl = {
		value,
		onChange: handleChange,
		onBlur: handleBlur,
		error,
	};

	useEffect(() => {
		if (validatedAddress !== undefined) {
			clearError();
			setValue(formatAddress(validatedAddress));
		} else if (validateAddressAttempted) {
			setError({ type: "error", message: "Invalid address" });
		}
	}, [validatedAddress]);

	return {
		validatedAddress,
		validateAddressIsLoading,
		validateAddressAttempted,
		validateAddress,
		addressFieldControl,
		setAddressValue: setValue,
	};
};

export const useGetFileUrl = () => {
	const { get } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (args: {
			baseUrl: string;
			asDownload: boolean;
			queryKey: QueryKey;
		}): Promise<string> => {
			const cachedData = queryClient.getQueryData<string>(args.queryKey);
			if (!!cachedData) return cachedData;
			return await get(`${args.baseUrl}?download=${args.asDownload ?? false}`, undefined, "text");
		},
		onSuccess: (data, { asDownload, queryKey }) => {
			if (asDownload) {
				downloadFileFromDataString(data);
			}
			queryClient.setQueryData(queryKey, data);
			queryClient.setQueryDefaults(queryKey, {
				staleTime: 60000,
				refetchOnWindowFocus: false,
				refetchOnMount: false,
				refetchOnReconnect: false,
			});
		},
	});

	return {
		getFileUrl: mutation.mutate,
		fileUrl: mutation.data,
		fileUrlError: mutation.error,
		fileUrlLoading: mutation.isPending,
		resetFileUrlError: mutation.reset,
	};
};

export const useGetFilesAsZip = () => {
	const { get } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (args: {
			files: { fileName: string; url: string; extension: string }[];
			queryKey: QueryKey;
			zippedFileName: string;
		}): Promise<{ fileName: string; url: string; extension: string }[]> => {
			return await Promise.all(
				args.files.map(async (file) => {
					const response = await get(`${file.url}?download=false`, undefined, "text");
					return { fileName: file.fileName, url: response, extension: file.extension };
				}),
			);
		},
		onSuccess: async (data, { queryKey, zippedFileName }) => {
			queryClient.setQueryData([queryKey], data);
			queryClient.setQueryDefaults(queryKey, {
				staleTime: 60000,
				refetchOnWindowFocus: false,
				refetchOnMount: false,
				refetchOnReconnect: false,
			});

			const zip = new JSZip();
			const downloadPromises = data.map(async (file) => {
				const response = await fetch(file.url, {
					mode: "cors",
				});
				const blob = await response.blob();
				zip.file(`${file.fileName}.${file.extension}`, blob);
			});
			await Promise.all(downloadPromises);
			const content = await zip.generateAsync({ type: "blob" });
			downloadFileFromDataBlob(content, `${zippedFileName}.zip`);
		},
	});

	return {
		getFilesAsZip: mutation.mutate,
		filesAsZip: mutation.data,
		filesAsZipError: mutation.error,
		filesAsZipLoading: mutation.isPending,
	};
};

export const useDownloadFileBuffer = () => {
	const { get } = useRequest();

	const downloadFileBuffer = (url: string, defaultFileName: string) => {
		return get(url, undefined, "response")
			.then((r) => {
				const disposition = r.headers.get("Content-Disposition");
				let filename = defaultFileName;
				if (disposition && disposition.indexOf("attachment") !== -1) {
					const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
					const matches = filenameRegex.exec(disposition);
					if (matches != null && matches[1]) {
						filename = decodeURIComponent(matches[1]);
					}
				}
				return r.blob().then((blob) => ({ blob, filename }));
			})
			.then(({ blob, filename }) => {
				downloadFileFromDataBlob(blob, filename);
				return null;
			});
	};

	const query = (url: string, defaultFileName: string, queryKey: QueryKey) => {
		return useQuery({
			queryKey: queryKey,
			queryFn: async () => await downloadFileBuffer(url, defaultFileName),
			retry: false,
			staleTime: 0,
			enabled: false,
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			refetchOnReconnect: false,
		});
	};

	return query;
};
